import React, {
  Component,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import Spline from '@splinetool/react-spline'
import gsap from 'gsap'

interface SplineParagonIdentityProps {
  className?: string
  style?: React.CSSProperties
  onLoad?: () => void
}

const SPLINE_URL = 'https://prod.spline.design/6Wq1Q7YGyM-iab9i/scene.splinecode'

interface SplineSceneBoundaryProps {
  children: ReactNode
  fallback: ReactNode
}

interface SplineSceneBoundaryState {
  hasError: boolean
}

class SplineSceneBoundary extends Component<SplineSceneBoundaryProps, SplineSceneBoundaryState> {
  state: SplineSceneBoundaryState = { hasError: false }

  static getDerivedStateFromError(): SplineSceneBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error) {
    console.warn('[SplineParagonIdentity] Spline render failed, using fallback:', error.message)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback
    }

    return this.props.children
  }
}

export function SplineParagonIdentity({ className, style, onLoad }: SplineParagonIdentityProps) {
  const [hasError, setHasError] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleLoad = useCallback(() => {
    onLoad?.()
  }, [onLoad])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const ctx = gsap.context(() => {
      gsap.fromTo(
        el,
        { opacity: 0, scale: 1.05 },
        {
          opacity: 1,
          scale: 1,
          duration: 2,
          ease: 'power2.out',
        }
      )

      const handleMouseMove = (e: MouseEvent) => {
        const rect = el.getBoundingClientRect()
        const x = ((e.clientX - rect.left) / rect.width - 0.5) * 6
        const y = ((e.clientY - rect.top) / rect.height - 0.5) * 6
        gsap.to(el, {
          rotationY: x,
          rotationX: -y,
          duration: 0.8,
          ease: 'power2.out',
          transformPerspective: 1200,
        })
      }

      el.addEventListener('mousemove', handleMouseMove)
      return () => el.removeEventListener('mousemove', handleMouseMove)
    }, el)

    return () => ctx.revert()
  }, [])

  const fallback = <SplineParagonIdentityStaticFallback />

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className ?? ''}`} style={style}>
      {hasError ? (
        fallback
      ) : (
        <SplineSceneBoundary fallback={fallback}>
          <Suspense fallback={<SplineParagonIdentityFallback />}>
            <Spline scene={SPLINE_URL} onLoad={handleLoad} onError={() => setHasError(true)} />
          </Suspense>
        </SplineSceneBoundary>
      )}
    </div>
  )
}

function SplineParagonIdentityStaticFallback() {
  return (
    <div
      aria-hidden
      className="absolute inset-0 overflow-hidden bg-[#1a0a2e]"
      data-testid="spline-paragon-identity-fallback"
    >
      <div className="absolute left-1/2 top-1/2 h-[560px] w-[900px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#d63384]/10 blur-[110px]" />
      <div className="absolute right-[12%] top-[16%] h-[260px] w-[260px] rounded-full border border-[#C9A84C]/20" />
      <div className="absolute bottom-[12%] left-[10%] h-px w-[46%] rotate-[-8deg] bg-[#C9A84C]/25" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-serif text-[clamp(5rem,18vw,14rem)] font-bold italic leading-none tracking-[-0.03em] text-white/[0.08]">
        Paragon
      </div>
    </div>
  )
}

function SplineParagonIdentityFallback() {
  return (
    <div
      className="spline-loading"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
      }}
      aria-busy="true"
      aria-label="Loading 3D scene"
    >
      <div className="spline-spinner" />
    </div>
  )
}
